import React, { useState, useEffect, useRef } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  format,
  isAfter,
  isBefore,
  isValid,
  parse,
  startOfDay,
  endOfDay,
  isFuture,
  isSameDay
} from 'date-fns';
import {
  DateRange,
  CaptionProps,
  useNavigation,
  DayPicker,
  SelectRangeEventHandler
} from 'react-day-picker';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import 'react-day-picker/dist/style.css';
import './AppFilterDate.scss';
import { Button, Divider, TextField, FormHelperText } from '@mui/material';

const AppFilterDate = ({ setFilter, futureDefault }) => {
  const [, setFromValue] = useState<string>('');
  const [, setToValue] = useState<string>('');
  const [selectedRange, setSelectedRange] = useState<DateRange>();
  const [hover, setHover] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarJustOpened, setCalendarJustOpened] = useState(false);
  const [onConfirm, setOnConfirm] = useState(false);

  const containerRef = useRef(null) as any;
  const calendarRef = useRef(null) as any;

  // track whether the selection has been confirmed (persist across open/close)
  // eslint-disable-next-line
  const [hasConfirmedSelection, setHasConfirmedSelection] = useState(false);

  // Describe currently selected dates for SR when the trigger is focused
  const getSelectedRangeAnnouncement = () => {
    const today = new Date();
    const endDefault = today;
    const from = selectedRange?.from ?? new Date('2023-01-01');
    const to = selectedRange?.to ?? endDefault;
    return `from ${format(from, 'MMMM d, yyyy')} to ${format(
      to,
      'MMMM d, yyyy'
    )}`;
  };

  // Trigger announcement when calendar opens
  useEffect(() => {
    if (hover) {
      setCalendarJustOpened(true);
      // Reset after a brief delay
      const timer = setTimeout(() => setCalendarJustOpened(false), 100);
      return () => clearTimeout(timer);
    }
  }, [hover]);

  // Add confirm handler
  const handleConfirm = () => {
    if (selectedRange?.from && selectedRange?.to) {
      const start = startOfDay(selectedRange.from);
      const end = endOfDay(selectedRange.to);
      setFilter([start, end]);
      setHasConfirmedSelection(true);
      setHover(false);
      setOnConfirm(true);
      setTimeout(() => {
        containerRef.current?.focus();
      }, 0);
    }
  };

  const selectedDate = () => {
    const formatDate = (date) => format(date, 'MMM dd yyyy');

    const from = selectedRange?.from
      ? formatDate(selectedRange.from)
      : 'Jan 1 2023';
    const today = new Date();
    const endDate = today;
    const to = selectedRange?.to
      ? formatDate(selectedRange.to)
      : formatDate(endDate);

    return `${from} - ${to}`;
  };

  // get only truly focusable elements inside the calendar
  const getFocusableElements = (): HTMLElement[] => {
    const calendarElement = calendarRef.current as HTMLElement | null;
    if (!calendarElement) return [];
    const selectors = [
      'button:not([disabled])',
      // '[href]',
      'input:not([disabled])',
      // 'select:not([disabled])',
      // 'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])'
    ].join(',');
    const nodes = Array.from(
      calendarElement.querySelectorAll(selectors)
    ) as HTMLElement[];

    // Filter out hidden elements
    return nodes.filter(
      (el) =>
        el.offsetParent !== null && // not display:none or in a hidden container
        getComputedStyle(el).visibility !== 'hidden'
    );
  };

  // Add focus trapping logic
  useEffect(() => {
    if (!hover) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setHover(false);
        containerRef.current?.focus();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (!focusableElements.length) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      }

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        const calendarElement = calendarRef.current as HTMLElement | null;
        if (!calendarElement) return;

        const activeEl = document.activeElement as HTMLElement | null;
        if (!activeEl) return;

        const isInsideCalendar = calendarElement.contains(activeEl);
        if (!isInsideCalendar) return;

        // Skip arrow key handling when focus is on input fields
        if (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA') return;

        // Stop page scroll AND DayPicker's own arrow key handler
        e.preventDefault();
        e.stopImmediatePropagation();

        const table = calendarElement.querySelector('.custom-calendar table');
        if (!table) return;

        // Layer 1: Caption row — [prevBtn, heading, nextBtn]
        const captionDiv = calendarElement.querySelector('.custom-calendar .rdp-caption');
        const captionButtons = captionDiv
          ? (Array.from(captionDiv.querySelectorAll('button:not([disabled])')) as HTMLElement[])
          : [];
        const headingSpan = captionDiv?.querySelector('span') as HTMLElement | null;

        // Make heading focusable
        if (headingSpan && !headingSpan.hasAttribute('tabindex')) {
          headingSpan.setAttribute('tabindex', '-1');
          headingSpan.setAttribute('role', 'heading');
          headingSpan.setAttribute('aria-level', '2');
        }

        const captionRow: HTMLElement[] = [];
        if (captionButtons[0]) captionRow.push(captionButtons[0]); // prev
        if (headingSpan) captionRow.push(headingSpan); // heading
        if (captionButtons[1]) captionRow.push(captionButtons[1]); // next

        // Layer 2: Weekday header row
        const weekdayHeaders = Array.from(
          table.querySelectorAll('thead th')
        ) as HTMLElement[];

        // Make weekday headers focusable
        weekdayHeaders.forEach((th) => {
          if (!th.hasAttribute('tabindex')) {
            th.setAttribute('tabindex', '-1');
          }
        });

        // Layer 3: Day buttons grid
        const dayButtons = Array.from(
          table.querySelectorAll('td button:not([disabled])')
        ) as HTMLElement[];

        if (!dayButtons.length) return;

        const isInCaption = captionRow.includes(activeEl);
        const isInWeekdays = weekdayHeaders.includes(activeEl);
        const isOnDayButton = activeEl.tagName === 'BUTTON' && activeEl.closest('td');

        if (isInCaption) {
          const idx = captionRow.indexOf(activeEl);

          if (e.key === 'ArrowRight') {
            const nextIdx = (idx + 1) % captionRow.length;
            captionRow[nextIdx].focus();
            return;
          }
          if (e.key === 'ArrowLeft') {
            const prevIdx = (idx - 1 + captionRow.length) % captionRow.length;
            captionRow[prevIdx].focus();
            return;
          }
          if (e.key === 'ArrowDown') {
            // Move to weekday header row (same column position or first)
            const colIdx = Math.min(idx, weekdayHeaders.length - 1);
            weekdayHeaders[colIdx].focus();
            return;
          }
          if (e.key === 'ArrowUp') {
            // Stay — nothing above caption
            return;
          }
          return;
        }

        if (isInWeekdays) {
          const idx = weekdayHeaders.indexOf(activeEl);

          if (e.key === 'ArrowRight') {
            const nextIdx = (idx + 1) % weekdayHeaders.length;
            weekdayHeaders[nextIdx].focus();
            return;
          }
          if (e.key === 'ArrowLeft') {
            const prevIdx = (idx - 1 + weekdayHeaders.length) % weekdayHeaders.length;
            weekdayHeaders[prevIdx].focus();
            return;
          }
          if (e.key === 'ArrowDown') {
            // Move to first row of day buttons, same column
            // Find the first day button in the same column (idx)
            const firstRowButtons = dayButtons.slice(0, 7);
            const targetIdx = Math.min(idx, firstRowButtons.length - 1);
            const targetDay = firstRowButtons[targetIdx];
            if (targetDay) {
              const span = targetDay.querySelector('span[aria-label]');
              if (span) {
                targetDay.setAttribute('aria-label', span.getAttribute('aria-label') || '');
              }
              dayButtons.forEach((btn) => btn.setAttribute('tabindex', '-1'));
              targetDay.setAttribute('tabindex', '0');
              targetDay.focus();
            }
            return;
          }
          if (e.key === 'ArrowUp') {
            // Move back to caption row
            const colIdx = Math.min(idx, captionRow.length - 1);
            captionRow[colIdx].focus();
            return;
          }
          return;
        }

        if (isOnDayButton) {
          const currentIndex = dayButtons.indexOf(activeEl);
          if (currentIndex === -1) return;

          let nextIndex = currentIndex;
          const cols = 7;

          if (e.key === 'ArrowRight') {
            nextIndex = currentIndex + 1;
          } else if (e.key === 'ArrowLeft') {
            nextIndex = currentIndex - 1;
          } else if (e.key === 'ArrowDown') {
            nextIndex = currentIndex + cols;
          } else if (e.key === 'ArrowUp') {
            nextIndex = currentIndex - cols;
          }

          // ArrowUp from first row → move to weekday headers
          if (nextIndex < 0 && e.key === 'ArrowUp') {
            const colIdx = currentIndex % cols;
            const targetIdx = Math.min(colIdx, weekdayHeaders.length - 1);
            activeEl.setAttribute('tabindex', '-1');
            weekdayHeaders[targetIdx].focus();
            return;
          }

          // Wrap cyclically within the day grid for left/right
          if (nextIndex < 0) {
            nextIndex = dayButtons.length - 1;
          } else if (nextIndex >= dayButtons.length) {
            nextIndex = 0;
          }

          const nextButton = dayButtons[nextIndex];

          // Ensure aria-label is on the button for NVDA
          const span = nextButton.querySelector('span[aria-label]');
          if (span) {
            nextButton.setAttribute('aria-label', span.getAttribute('aria-label') || '');
          }

          // Roving tabindex
          activeEl.setAttribute('tabindex', '-1');
          nextButton.setAttribute('tabindex', '0');
          nextButton.focus();
          return;
        }

        // For any other element (clear, confirm, inputs) — don't move with arrows
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [hover]);

  // Focus first element when calendar opens
  useEffect(() => {
    if (hover && calendarRef.current) {
      setTimeout(() => {
        const focusable = getFocusableElements();
        const firstFocusable = focusable[0];
        firstFocusable?.focus();
      }, 50);
    }
  }, [hover]);

  // clear selection only if calendar closes without a confirmed selection
  useEffect(() => {
    if (!hover) {
      if (!onConfirm) {
        setSelectedRange(undefined);
        setFromValue('--');
        setToValue('--');
        setCurrentMonth(new Date());
      } else if (!hasConfirmedSelection) {
        setOnConfirm(false);
      }
    }
    // eslint-disable-next-line
  }, [hover, hasConfirmedSelection]);

  return (
    <>
      {/* Offscreen description announced when the trigger receives focus */}
      <span
        id="date-filter-desc"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {getSelectedRangeAnnouncement()}
      </span>

      <div
        style={{ display: 'flex' }}
        onMouseOver={() => setHover(true)}
        onMouseOut={() => setHover(false)}
        onClick={() => setHover(!hover)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setHover(!hover);
        }}
        tabIndex={0}
        role="button"
        aria-expanded={hover}
        aria-label="Filter by date"
        aria-describedby="date-filter-desc"
        aria-hidden={hover}
        ref={containerRef}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'space-between',
            padding: '6px 10px',
            width: '100%',
            height: '40px',
            border: '2px solid #EFEFEF',
            borderRadius: '0 5px 5px 0',
            cursor: 'pointer'
          }}
        >
          <span>{selectedDate()}</span>
          <CalendarTodayIcon
            fontSize="small"
            sx={{ marginTop: '2px', marginLeft: '10px' }}
          />
        </div>
      </div>
      {hover && (
        <Box
          className="date-head"
          role="application"
          onMouseOver={() => setHover(true)}
          onMouseOut={() => setHover(false)}
          onFocus={() => setHover(true)}
          // Remove the onBlur handler that was closing the calendar
          ref={calendarRef}
          // aria-modal="true"
          aria-label="Date range picker"
        >
          <div className="calendar-container">
            <CustomDate
              selectedRange={selectedRange}
              setSelectedRange={setSelectedRange}
              setFromValue={setFromValue}
              setToValue={setToValue}
              futureDefault={futureDefault}
              onConfirm={handleConfirm}
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              calendarJustOpened={calendarJustOpened}
              setFilter={setFilter}
              // let Clear reset the confirmed flag
              onClearConfirmed={() => setHasConfirmedSelection(false)}
            />
          </div>
        </Box>
      )}
    </>
  );
};

function CustomDate({
  selectedRange,
  setSelectedRange,
  setFromValue,
  setToValue,
  futureDefault,
  onConfirm,
  currentMonth,
  setCurrentMonth,
  calendarJustOpened,
  setFilter,
  onClearConfirmed
}: {
  selectedRange: DateRange | undefined;
  setSelectedRange: (range: DateRange | undefined) => void;
  setFromValue: (v: string) => void;
  setToValue: (v: string) => void;
  futureDefault: Date;
  onConfirm: () => void;
  currentMonth: Date;
  setCurrentMonth: (month: Date) => void;
  calendarJustOpened: boolean;
  setFilter: (range: [Date, Date] | undefined) => void;
  onClearConfirmed: () => void;
}) {
  const [startDateInput, setStartDateInput] = useState<string>('');
  const [endDateInput, setEndDateInput] = useState<string>('');
  const [monthAnnouncement, setMonthAnnouncement] = useState<string>('');
  const { t, i18n } = useTranslation();

  const json = t('releaseNotesDayPicker', { returnObjects: true });

  // Add proper ARIA roles to calendar table for better screen reader support
  useEffect(() => {
    const calendarTable = document.querySelector('.custom-calendar table');
    if (calendarTable) {
      calendarTable.setAttribute('role', 'grid');
      calendarTable.setAttribute('aria-label', 'Calendar grid');
      calendarTable.setAttribute('aria-roledescription', '');

      // Set role on tbody
      const tbody = calendarTable.querySelector('tbody');
      if (tbody) {
        tbody.setAttribute('role', 'rowgroup');
      }

      // Set role on thead
      const thead = calendarTable.querySelector('thead');
      if (thead) {
        thead.setAttribute('role', 'rowgroup');
      }

      // Set role on all rows
      const rows = calendarTable.querySelectorAll('tr');
      rows.forEach((row) => {
        row.setAttribute('role', 'row');
      });

      // Set role on all header cells
      const headerCells = calendarTable.querySelectorAll('th');
      headerCells.forEach((cell) => {
        cell.setAttribute('role', 'columnheader');
        cell.setAttribute('aria-roledescription', '');
      });

      // Set role on all cells
      const cells = calendarTable.querySelectorAll('td');
      cells.forEach((cell) => {
        cell.setAttribute('role', 'gridcell');
        cell.setAttribute('aria-roledescription', '');
      });
    }
  }, [currentMonth]); // Re-run when month changes

  // focus and format announcement state
  const [startFocused, setStartFocused] = useState(false);
  const [endFocused, setEndFocused] = useState(false);
  // announce converted date once after input converts
  const [dateAnnouncement, setDateAnnouncement] = useState<string>('');
  // announce selection (start/end) when user picks dates in the calendar
  const [selectionAnnouncement, setSelectionAnnouncement] =
    useState<string>('');
  // announce when dates are cleared
  const [clearAnnouncement, setClearAnnouncement] = useState<string>('');

  // Announce month when calendar opens or month changes
  useEffect(() => {
    const monthNames = {
      en: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ],
      fr: [
        'janvier',
        'février',
        'mars',
        'avril',
        'mai',
        'juin',
        'juillet',
        'août',
        'septembre',
        'octobre',
        'novembre',
        'décembre'
      ]
    };

    const currentLanguage = i18n.language || 'en';
    const monthName = monthNames[currentLanguage][currentMonth.getMonth()];
    const year = currentMonth.getFullYear();

    // Clear announcement first, then set it to trigger screen reader
    setMonthAnnouncement('');
    setTimeout(() => {
      setMonthAnnouncement(`${monthName} ${year}`);
    }, 50);
  }, [currentMonth, i18n.language, calendarJustOpened]);

  // Update input values when calendar selection changes
  useEffect(() => {
    if (selectedRange?.from && !startFocused) {
      setStartDateInput(format(selectedRange.from, 'eee MMM dd'));
    }
    if (selectedRange?.to && !endFocused) {
      setEndDateInput(format(selectedRange.to, 'eee MMM dd'));
    }
  }, [selectedRange, startFocused, endFocused]);

  // Replace shared error with per-field errors
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startError, setStartError] = useState(false);
  const [endError, setEndError] = useState(false);

  useEffect(() => {
    // Show a single common error if any field is invalid
    if (startError || endError) {
      setErrorMessage(`${json['errMsg']}`);
    } else {
      setErrorMessage('');
    }
  }, [startError, endError, json]);

  const handleRangeSelect: SelectRangeEventHandler = (range) => {
    // Detect if day-picker auto-swapped the dates (user selected end before start)
    const wasSwapped =
      selectedRange?.from &&
      !selectedRange?.to &&
      range?.from &&
      range?.to &&
      range.to.getTime() === selectedRange.from.getTime() &&
      range.from.getTime() !== selectedRange.from.getTime();

    // Detect 3rd click on same single-day to toggle off and clear inputs
    const prevFrom = selectedRange?.from;
    const prevTo = selectedRange?.to;
    const prevWasSingleDay =
      !!prevFrom && !!prevTo && isSameDay(prevFrom, prevTo);

    const toggledOffSingleDay =
      !range ||
      (!!range?.from &&
        !range?.to &&
        prevWasSingleDay &&
        !!prevFrom &&
        isSameDay(range.from, prevFrom));

    if (!range) {
      handleClear();
    }

    if (toggledOffSingleDay) {
      setSelectedRange(undefined);
      setStartError(false);
      setEndError(false);
      setFromValue('--');
      setToValue('--');
      setStartDateInput('');
      setEndDateInput('');
      setSelectionAnnouncement('');
      setTimeout(() => {
        setSelectionAnnouncement(`${json['clearDates']}`);
      }, 50);
      return;
    }

    const normalized = {
      from: range?.from ? startOfDay(range.from) : undefined,
      to: range?.to ? endOfDay(range.to) : undefined
    };

    setSelectedRange(normalized);
    setStartError(false);
    setEndError(false);

    const fromDate = normalized.from;
    const toDate = normalized.to;

    if (fromDate) {
      const from = format(fromDate, 'eee MMM dd').toLowerCase();
      setFromValue(from);
    } else {
      setFromValue('--');
      setStartDateInput(''); // clear input when start is unset
    }

    if (toDate) {
      const to = format(toDate, 'eee MMM dd').toLowerCase();
      setToValue(to);
    } else {
      setToValue('--');
      setEndDateInput(''); // clear input when end is unset
    }

    // Screen reader announcement for first and second selection
    setSelectionAnnouncement('');
    if (fromDate && !toDate) {
      setTimeout(() => {
        setSelectionAnnouncement(
          `${format(fromDate, 'MMMM d, yyyy')} ${json['startDate']}`
        );
      }, 50);
    } else if (fromDate && toDate) {
      setTimeout(() => {
        setSelectionAnnouncement(
          `${format(toDate, 'MMMM d, yyyy')} ${json['endDate']}`
        );
      }, 50);
    }
    setTimeout(() => {
      if (wasSwapped) {
        setSelectionAnnouncement(`${json['swap']}`);
      }
    }, 100);
  };

  const CustomDayContent = (props: any) => {
    const { date } = props;
    return (
      <span aria-label={format(date, 'd MMM yyyy')}>{date.getDate()}</span>
    );
  };

  const formatDateInputValue = (value: string) => {
    // Remove all non-digits
    const numbers = value.replace(/\D/g, '');

    // Format as dd/mm/yyyy - NO auto-correction
    if (numbers.length <= 2) {
      const dd = numbers;
      return dd;
    } else if (numbers.length <= 4) {
      const dd = numbers.slice(0, 2);
      const mm = numbers.slice(2);
      return `${dd}/${mm}`;
    } else if (numbers.length <= 8) {
      const dd = numbers.slice(0, 2);
      const mm = numbers.slice(2, 4);
      const yyyy = numbers.slice(4, 8);
      return `${dd}/${mm}/${yyyy}`;
    } else {
      const dd = numbers.slice(0, 2);
      const mm = numbers.slice(2, 4);
      const yyyy = numbers.slice(4, 8);
      return `${dd}/${mm}/${yyyy}`;
    }
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/[^0-9]/g, '');

    if (/^\d/.test(value)) {
      const formattedValue = formatDateInputValue(value);
      setStartDateInput(formattedValue);

      // Check for invalid day immediately
      if (formattedValue.length >= 2) {
        const dd = parseInt(formattedValue.slice(0, 2));
        if (dd > 31 || dd === 0) {
          setStartError(true);
          return;
        }
      }

      // Check for invalid month immediately
      if (formattedValue.length >= 5) {
        const mm = parseInt(formattedValue.slice(3, 5));
        if (mm > 12 || mm === 0) {
          setStartError(true);
          return;
        }
      }

      if (formattedValue.length !== 10) {
        setStartError(false);
        return;
      }

      const date = parse(formattedValue, 'dd/MM/yyyy', new Date());

      if (!isValid(date)) {
        setStartError(true);
        return;
      }

      // Check if date is in the future
      if (isFuture(date)) {
        setStartError(true);
        return;
      }

      setStartError(false);
      const start = startOfDay(date);
      setSelectedRange({
        from: start,
        to:
          selectedRange?.to && isAfter(start, selectedRange.to)
            ? start
            : selectedRange?.to
      });

      setFromValue(format(start, 'eee MMM dd').toLowerCase());
      setDateAnnouncement('');
      setTimeout(() => {
        setDateAnnouncement(format(date, 'eee MMM dd').toLowerCase());
      }, 50);
    } else {
      setStartDateInput(value);
      // Clear selection when input is emptied
      if (value.trim() === '') {
        setStartError(false);
        setFromValue('--');
        setSelectedRange(undefined);
      }
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/[^0-9]/g, '');

    if (/^\d/.test(value)) {
      const formattedValue = formatDateInputValue(value);
      setEndDateInput(formattedValue);

      // Check for invalid day immediately
      if (formattedValue.length >= 2) {
        const dd = parseInt(formattedValue.slice(0, 2));
        if (dd > 31 || dd === 0) {
          setEndError(true);
          return;
        }
      }

      // Check for invalid month immediately
      if (formattedValue.length >= 5) {
        const mm = parseInt(formattedValue.slice(3, 5));
        if (mm > 12 || mm === 0) {
          setEndError(true);
          return;
        }
      }

      if (formattedValue.length !== 10) {
        setEndError(false);
        return;
      }

      const date = parse(formattedValue, 'dd/MM/yyyy', new Date());

      if (!isValid(date)) {
        setEndError(true);
        return;
      }

      // Check if date is in the future
      if (isFuture(date)) {
        setEndError(true);
        return;
      }

      // If end date < start date, swap: new from = entered end date, new to = previous start date
      if (selectedRange?.from && isBefore(date, selectedRange.from)) {
        const newFrom = startOfDay(date);
        const newTo = endOfDay(selectedRange.from);

        setEndError(false);
        setSelectedRange({
          from: newFrom,
          to: newTo
        });

        // Keep inputs in editable format while focused
        setStartDateInput(format(newFrom, 'dd/MM/yyyy'));
        setEndDateInput(format(newTo, 'dd/MM/yyyy'));

        // Update SR-visible values
        setFromValue(format(newFrom, 'eee MMM dd').toLowerCase());
        setToValue(format(newTo, 'eee MMM dd').toLowerCase());

        setDateAnnouncement('');
        setTimeout(() => {
          setDateAnnouncement(`${json['swap']}`);
        }, 50);
        return;
      }

      setEndError(false);
      const end = endOfDay(date);
      setSelectedRange({
        from: selectedRange?.from,
        to: end
      });

      setToValue(format(end, 'eee MMM dd').toLowerCase());
      setDateAnnouncement('');
      setTimeout(() => {
        setDateAnnouncement(format(date, 'eee MMM dd').toLowerCase());
      }, 50);
    } else {
      setEndDateInput(value);
      // Clear selection when input is emptied
      if (value.trim() === '') {
        setEndError(false);
        setToValue('--');
        setSelectedRange(undefined);
      }
    }
  };

  const handleClear = () => {
    setSelectedRange(undefined);
    setFromValue('--');
    setToValue('--');
    setStartDateInput('');
    setEndDateInput('');
    setStartError(false);
    setEndError(false);
    setErrorMessage('');
    setFilter(undefined);
    onClearConfirmed();
    // Announce dates cleared
    setClearAnnouncement('');
    setTimeout(() => {
      setClearAnnouncement(`${json['clearDates']}`);
    }, 50);
  };

  // Update useEffect to convert display format back to editable format on focus
  useEffect(() => {
    if (selectedRange?.from && startFocused) {
      setStartDateInput(format(selectedRange.from, 'dd/MM/yyyy'));
    }
    if (selectedRange?.to && endFocused) {
      setEndDateInput(format(selectedRange.to, 'dd/MM/yyyy'));
    }
  }, [startFocused, endFocused, selectedRange]);

  return (
    <>
      {/* Screen reader announcement for current month */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {monthAnnouncement}
      </div>

      {/* SR live region for converted date announcement */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {dateAnnouncement}
      </div>

      {/* SR live region for selection announcement */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {selectionAnnouncement}
      </div>

      {/* SR live region for clear announcement */}
      <div
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {clearAnnouncement}
      </div>

      <div aria-label="Date picker calendar">
        <DayPicker
          mode="range"
          showOutsideDays
          selected={selectedRange}
          onSelect={handleRangeSelect}
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          disabled={[
            { after: new Date() } // Disable all dates after today
          ]}
          components={{
            Head: CustomHead,
            Caption: CustomCaption,
            DayContent: CustomDayContent
          }}
          className="custom-calendar"
        />
      </div>

      {/* Clear dates */}
      <div
        style={{
          textAlign: 'right',
          marginTop: 8,
          background: '#F2F4F7',
          width: '280px',
          height: '48px',
          marginLeft: '-10px'
        }}
      >
        <Button
          variant="text"
          onClick={handleClear}
          sx={{
            textTransform: 'none',
            textDecoration: 'underline',
            color: '#1460AA',
            fontWeight: 700,
            fontSize: '14px',
            lineHeight: '20px',
            mt: '5px',
            mr: '10px',
            '&:focus-visible': {
              outline: '2px solid #000',
              outlineOffset: '2px'
            }
          }}
        >
          {json['clearButton']}
        </Button>
      </div>

      {/* Start / End date inputs */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginTop: 16,
          width: '100%'
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            id="start-date-label"
            className="label"
            style={{
              fontSize: '14px',
              width: '100%',
              paddingLeft: '10px',
              fontWeight: 600,
              color: '#717173'
            }}
          >
            {json['startDate']}:
          </div>
          <TextField
            type="text"
            size="small"
            value={startDateInput}
            onChange={handleStartDateChange}
            placeholder={startFocused ? 'DD/MM/YYYY' : '--'}
            onFocus={() => {
              setStartFocused(true);
              if (selectedRange?.from) {
                setStartDateInput(format(selectedRange.from, 'dd/MM/yyyy'));
              }
            }}
            onBlur={() => {
              setStartFocused(false);
              const digits = startDateInput.replace(/\D/g, '');
              // If not empty and not 8 digits (DDMMYYYY), flag error
              if (startDateInput.trim() !== '' && digits.length !== 8) {
                setStartError(true);
                return;
              }
              if (startDateInput.trim() === '') {
                setFromValue('--');
                setSelectedRange(undefined);
                return;
              }
              if (selectedRange?.from) {
                setStartDateInput(format(selectedRange.from, 'eee MMM dd'));
              }
            }}
            label="" // Prevents double announcement
            inputProps={{
              maxLength: 12,
              'aria-labelledby': 'start-date-label',
              'aria-describedby': 'start-date-format',
              'aria-required': 'false'
            }}
            // error={startError}
            helperText={' '}
            sx={{
              width: '100%',
              pl: '10px',
              '& .MuiOutlinedInput-root': {
                border: 'none',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' }
              },
              '& .MuiInputBase-input': {
                textAlign: 'left',
                padding: '8px 0',
                color: '#000000',
                fontSize: '16px',
                fontWeight: '600'
              }
            }}
          />
          <span
            id="start-date-format"
            style={{
              position: 'absolute',
              left: '-10000px',
              width: '1px',
              height: '1px',
              overflow: 'hidden'
            }}
          >
            {json['dateFormat']}
          </span>
        </div>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ color: '#A3A4A6', height: '50px' }}
        />

        <div style={{ flex: 1 }}>
          <div
            id="end-date-label"
            className="label"
            style={{
              fontSize: '14px',
              width: '95%',
              fontWeight: 600,
              color: '#717173'
            }}
          >
            {json['endDate']}:
          </div>
          <TextField
            type="text"
            size="small"
            value={endDateInput}
            onChange={handleEndDateChange}
            placeholder={endFocused ? 'DD/MM/YYYY' : '--'}
            onFocus={() => {
              setEndFocused(true);
              if (selectedRange?.to) {
                setEndDateInput(format(selectedRange.to, 'dd/MM/yyyy'));
              }
            }}
            onBlur={() => {
              setEndFocused(false);
              const digits = endDateInput.replace(/\D/g, '');
              // If not empty and not 8 digits (DDMMYYYY), flag error
              if (endDateInput.trim() !== '' && digits.length !== 8) {
                setEndError(true);
                return;
              }
              if (endDateInput.trim() === '') {
                setToValue('--');
                setSelectedRange(undefined);
                return;
              }
              if (selectedRange?.to) {
                setEndDateInput(format(selectedRange.to, 'eee MMM dd'));
              }
            }}
            label=""
            inputProps={{
              maxLength: 12,
              'aria-labelledby': 'end-date-label',
              'aria-describedby': 'end-date-format',
              'aria-required': 'false'
            }}
            // error={endError}
            helperText={' '}
            sx={{
              width: '95%',
              '& .MuiOutlinedInput-root': {
                border: 'none',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' }
              },
              '& .MuiInputBase-input': {
                textAlign: 'left',
                padding: '8px 0',
                color: '#000000',
                fontSize: '16px',
                fontWeight: '600'
              }
            }}
          />
          <span
            id="end-date-format"
            style={{
              position: 'absolute',
              left: '-10000px',
              width: '1px',
              height: '1px',
              overflow: 'hidden'
            }}
          >
            {json['dateFormat']}
          </span>
        </div>
      </div>

      {/* Common error message for both fields */}
      <FormHelperText
        error
        aria-live="assertive"
        sx={{ textAlign: 'center', mt: '-20px', mb: '10px' }}
      >
        {errorMessage || ' '}
      </FormHelperText>

      {/* Confirm */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          fullWidth
          variant="primary"
          size="md"
          disabled={
            !(selectedRange?.from && selectedRange?.to) || !!errorMessage
          }
          onClick={onConfirm}
          aria-label={json['confirmAria']}
        >
          {json['confirm']}
        </Button>
      </div>
    </>
  );
}

const CustomHead = () => {
  const week = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const weekFull = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday'
  ];
  return (
    <thead role="presentation">
      <tr role="row">
        {week.map((day, i) => (
          <th
            key={`${day}-${i}`}
            className="custom-head"
            role="columnheader"
            aria-label={weekFull[i]}
          >
            {day}
          </th>
        ))}
      </tr>
    </thead>
  );
};

const CustomCaption = (props: CaptionProps) => {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const { t, i18n } = useTranslation();

  const json = t('releaseNotesDayPicker', { returnObjects: true });

  const monthNames = {
    en: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ],
    fr: [
      'janvier',
      'février',
      'mars',
      'avril',
      'mai',
      'juin',
      'juillet',
      'août',
      'septembre',
      'octobre',
      'novembre',
      'décembre'
    ]
  };
  const currentLanguage = i18n.language || 'en';
  const monthName = monthNames[currentLanguage][props.displayMonth.getMonth()];
  return (
    <div
      // role="application"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}
    >
      <IconButton
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        aria-label={json['prevMonth']}
        sx={{
          '&:focus-visible': {
            outline: '2px solid #000',
            outlineOffset: '2px'
          }
        }}
      >
        <ChevronLeftIcon fontSize="medium" sx={{ color: 'black' }} />
      </IconButton>
      <span
        style={{
          // fontFamily: 'Open Sans',
          color: '#2A2A2A',
          fontSize: '20px'
        }}
      >
        {`${monthName} ${props.displayMonth.getFullYear()}`}
      </span>
      <IconButton
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        aria-label={json['nxtMonth']}
        sx={{
          '&:focus-visible': {
            outline: '2px solid #000',
            outlineOffset: '2px'
          }
        }}
      >
        <ChevronRightIcon fontSize="medium" sx={{ color: 'black' }} />
      </IconButton>
    </div>
  );
};

export default AppFilterDate;
